import i18n from '../i18n/config';

/**
 * Translates backend error keys to localized messages
 * @param {string} errorKey - The error key from backend (e.g., "unauthorized", "patientNotFound")
 * @param {object} params - Optional parameters for interpolation (e.g., { fileId: "123" })
 * @returns {string} - Translated error message
 */
export const translateError = (errorKey, params = {}) => {
  if (!errorKey) {
    return i18n.t('errors:genericError');
  }

  // If errorKey is already a translated message (doesn't exist in errors namespace), return as is
  try {
    const translated = i18n.t(`errors:${errorKey}`, params);
    // If translation returns the key itself (not found), it might be a direct message
    if (translated === `errors:${errorKey}`) {
      return errorKey; // Return original if not found in translations
    }
    return translated;
  } catch (error) {
    // If translation fails, return the original key or a generic error
    return errorKey || i18n.t('errors:genericError');
  }
};

/**
 * Extracts error key from backend response
 * Backend can return: { error: "key" }, { message: "key" }, or just "key"
 * @param {any} errorResponse - Error response from backend
 * @returns {string} - Error key
 */
export const extractErrorKey = (errorResponse) => {
  if (!errorResponse) return null;
  
  if (typeof errorResponse === 'string') {
    return errorResponse;
  }
  
  if (typeof errorResponse === 'object') {
    return errorResponse.error || errorResponse.message || errorResponse.key || null;
  }
  
  return null;
};

/**
 * Translates backend error response to localized message
 * @param {any} errorResponse - Error response from backend (can be string, object with error/message/key)
 * @param {object} params - Optional parameters for interpolation (will be merged with params from errorResponse)
 * @returns {string} - Translated error message
 */
export const translateBackendError = (errorResponse, params = {}) => {
  const errorKey = extractErrorKey(errorResponse);
  if (!errorKey) {
    return i18n.t('errors:genericError');
  }
  
  // Extract additional parameters from errorResponse (like fileId, etc.)
  const mergedParams = { ...params };
  if (typeof errorResponse === 'object' && errorResponse !== null) {
    // Copy all properties except 'error', 'message', 'key' to params
    Object.keys(errorResponse).forEach(key => {
      if (key !== 'error' && key !== 'message' && key !== 'key') {
        mergedParams[key] = errorResponse[key];
      }
    });
  }
  
  return translateError(errorKey, mergedParams);
};
